<template>
  <div class="space-y-2">
    <div v-loading="loading" class="brand-card-lg">
      <div>
        <p class="title">Members</p>
        <p class="subtitle mt-3 text-brand-brown text-lg font-medium">Manage who has access to Churnkey.</p>
      </div>

      <el-button v-can-enable="'users:write'" class="mt-4" type="primary" @click="showInvite = true">+ Invite via Email</el-button>
      <el-table :data="team" style="width: 100%" class="mt-4 font-semibold">
        <el-table-column prop="name" label="Name"> </el-table-column>
        <el-table-column prop="email" label="Email">
          <template #default="scope">
            <p v-if="scope.row.emailConfirmed">{{ scope.row.email }}</p>
            <p class="flex items-center space-x-2" v-else>
              <span class="inline font-semibold">{{ scope.row.email }}</span>
              <el-tag class="bg-fuschia-200 text-fuschia-800 pt-1 h-6 px-4 font-bold border-0 text-xs rounded-xl inline"> Pending </el-tag>
            </p>
          </template>
        </el-table-column>
        <el-table-column prop="role" label="Role">
          <template #default="scope">
            <p class="font-semibold">{{ roleLabel(scope.row.role) }}</p>
          </template>
        </el-table-column>
        <el-table-column prop="issueNotifications" label="Developer Notifications">
          <template #header>
            <el-tooltip content="Receive notifications when an issue is detected" placement="top">
              <p>Developer Notifications</p>
            </el-tooltip>
          </template>
          <template #default="scope">
            <!-- a checkbox for receiving developer notifications -->
            <el-checkbox
              v-can-enable="'users:write'"
              v-model="scope.row.developerNotifications"
              @change="(developerNotifications) => updateUser(scope.row._id, { developerNotifications })"
            />
          </template>
        </el-table-column>
        <el-table-column> </el-table-column>
        <el-table-column width="60">
          <template #default="scope">
            <el-dropdown trigger="click" v-can-show="'users:write'">
              <div class="px-2">
                <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
              </div>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-can-show="'users:write'" icon="el-icon-s-tools" @click.native="goToUserRole(scope.row)"
                  >Edit Role</el-dropdown-item
                >
                <el-dropdown-item class="text-red-700" v-can-show="'users:write'" icon="el-icon-delete" @click.native="goToRemoveUser(scope.row)">
                  <span class="font-medium">Remove</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <!-- Edit Role Dialog -->
      <el-dialog
        class="p-0"
        custom-class="container max-w-2xl rounded-lg px-0 py-0 no-padding"
        :visible.sync="showEditUserRole"
        :close-on-click-modal="true"
        :append-to-body="true"
      >
        <EditUserRole
          :user="editUser"
          @updated="fetchMembers"
          @close="showEditUserRole = false"
          :key="editUser ? editUser._id + editUser.role : '-1'"
        />
      </el-dialog>

      <!-- Invite User Dialog -->
      <el-dialog
        class="p-0"
        custom-class="container max-w-lg rounded-lg px-0 py-0 no-padding"
        :visible.sync="showInvite"
        :close-on-click-modal="true"
        :append-to-body="true"
      >
        <InviteForm @close="showInvite = false" @sent="onInvite" />
      </el-dialog>

      <!-- Delete User Dialog -->
      <el-dialog
        class="p-0"
        custom-class="container max-w-xl rounded-lg px-0 py-0 no-padding"
        :visible.sync="showRemoveUser"
        :close-on-click-modal="true"
        :append-to-body="true"
      >
        <div class="px-4 py-8 space-y-3" v-loading="loading">
          <p class="font-bold text-xl">Confirm Member Removal</p>
          <p class="text-brand-brown text-base">
            This will remove {{ removeUser && removeUser.name ? removeUser.name : 'them' }} from your organization.
          </p>
        </div>
        <div class="border-t border-brand-light-brown bg-brand-sand py-4 rounded-b-lg">
          <div class="flex justify-end px-8 space-x-4">
            <el-button type="text" @click="showRemoveUser = false">Go Back</el-button>
            <el-button type="danger" @click="handleRemoveUser(removeUser._id)" :disabled="loading">Remove</el-button>
          </div>
        </div>
      </el-dialog>
    </div>
    <!-- Show 2FA setting to OWNER only: -->
    <div class="brand-card-lg" v-if="isOwner" v-loading="loading2FA">
      <div>
        <p class="title">Two-Factor Authentication (2FA)</p>
        <p class="subtitle mt-3 text-brand-brown text-lg font-medium">
          Require Two-Factor Authentication via email verification codes for all members of your team.
        </p>
      </div>
      <div>
        <div class="md:w-1/2 mt-4">
          <div class="flex justify-start space-x-8">
            <el-popconfirm
              icon-color="#f7b200"
              @onConfirm="enable2FA"
              :disabled="require2FA"
              title="Please note, you will have to contact support to disable 2FA."
              cancel-button-text="Back"
              confirm-button-text="Require 2FA"
            >
              <el-switch
                v-can-enable="'users:write'"
                slot="reference"
                active-text="Enabled"
                inactive-text="Disabled"
                :value="require2FA"
                :disabled="lock2FASwitch"
              >
              </el-switch>
            </el-popconfirm>
          </div>
        </div>
      </div>
      <div v-if="require2FA">
        <p class="text-sm mt-4 text-gray-800">
          To disable Two-Factor Authentication, please contact us at
          <b><a target="_blank" href="mailto:support@churnkey.co?subject=Disable 2FA">support@churnkey.co</a></b
          >.
        </p>
      </div>
      <div v-if="!emailConfirmed && !require2FA">
        <p class="text-sm mt-2 text-gray-800">
          You must <b><router-link to="/settings/account">verify your email address</router-link></b> before enabling 2FA.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import OrgService from '@/api/org.service';
import UserService from '@/api/user.service';
import InviteForm from '@/components/Settings/InviteForm.vue';
import EditUserRole from '@/components/Settings/EditUserRole.vue';
import { SYNC_ORG, SYNC_USER_RECORD } from '@/store/action-types';
import { mapGetters } from 'vuex';
import { roleDetails } from '@/store/modules/permissions';

export default {
  components: {
    InviteForm,
    EditUserRole,
  },
  data() {
    return {
      loading: true,
      loading2FA: true,
      error: '',
      team: null,
      showInvite: false,
      showRoleChangeModal: false, // legacy
      editUser: null,
      showEditUserRole: false,
      showRemoveUser: false,
      removeUser: null,
    };
  },
  watch: {
    $route: 'fetchMembers',
  },
  async created() {
    await this.fetchMembers();
    if (!this.$store.state.user) {
      await this.$store.dispatch(SYNC_USER_RECORD);
    }
    if (!this.$store.state.org) {
      await this.$store.dispatch(SYNC_ORG);
    }
    this.loading = false;
    this.loading2FA = false;
  },
  computed: {
    ...mapGetters(['isOwner']),
    org() {
      return this.$store.state.org;
    },
    userRole() {
      return this.$store.state.user.role;
    },
    require2FA() {
      if (!this.org) return false;
      return this.org.require2FA;
    },
    emailConfirmed() {
      // moved from data to computed so that we show the updated value after confirming email:
      return this.$store.state.user.emailConfirmed;
    },
    lock2FASwitch() {
      const alreadyEnabled = this.require2FA;
      const emailNotConfirmed = !this.emailConfirmed;
      return alreadyEnabled || emailNotConfirmed;
    },
  },
  methods: {
    roleLabel(role) {
      return roleDetails[role]?.label || role;
    },
    async fetchMembers() {
      try {
        this.team = await OrgService.getMembers(this.$store.state.user.org);
      } catch (error) {
        console.log(error);
      }
    },
    async onInvite() {
      this.loading = true;
      await this.fetchMembers();
      this.loading = false;
    },
    async updateUser(userId, update) {
      try {
        await UserService.updateUserProperties(userId, update);
      } catch (error) {
        this.$notify({ title: `Error updating user, please refresh page`, type: 'error' });
      }
    },
    goToUserRole(user) {
      this.editUser = user;
      this.showEditUserRole = true;
    },
    goToRemoveUser(user) {
      this.removeUser = user;
      this.showRemoveUser = true;
    },
    async handleRemoveUser(userId) {
      this.loading = true;
      try {
        await UserService.deleteUser(userId);
      } catch (error) {
        this.$notify({ title: `Error removing user, please refresh page`, type: 'error' });
      } finally {
        await this.fetchMembers();
        this.showRemoveUser = false;
        this.removeUser = null;
        this.loading = false;
      }
    },
    async enable2FA() {
      this.loading2FA = true;
      try {
        await OrgService.enable2FA(this.$store.state.user.org);
        await this.$store.dispatch('SYNC_ORG');
        this.$notify({ title: `Two-Factor Authentication has been enabled!`, type: 'success' });
      } catch (error) {
        this.$notify({ title: `Error: ${error}`, type: 'error' });
      } finally {
        this.loading2FA = false;
      }
    },
  },
};
</script>
